import moment from 'moment';
import { useCallback, useRef, useState } from 'react';
import { SUBSCRIPTION_TYPES, TalentLevel, TalentType, US_STATES, UserType } from './enums';
import { createImageVariantConfig } from './sdkLoader';
import { IMAGE_VARIANTS } from './constants';
import { parseChatSystemMessage } from './chatSystemMessage';

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const isWindowDefined = () => {
  return typeof window !== 'undefined';
};

export function isFunction(value) {
  return typeof value === 'function';
}

export const useStateRef = initialState => {
  const [state, setState] = useState(initialState);
  const ref = useRef(initialState);

  const dispatch = useCallback(stateToSet => {
    ref.current = isFunction(stateToSet) ? stateToSet(ref.current) : stateToSet;
    setState(ref.current);
  }, []);

  return [state, dispatch, ref];
};
export const shuffleArray = array => {
  const shuffledArray = [...array];
  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }
  return shuffledArray;
};

export const renameObjKeyToValue = arr => {
  return arr.map(item => {
    const { key, ...rest } = item;
    return { value: key, ...rest };
  });
};

export const capitalizeWords = str =>
  str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const capitalizeFirstLetter = (text = '') => {
  if (typeof text !== 'string' || text.length === 0) return '';
  return text[0].toUpperCase() + text.slice(1);
};

export const toLowerCase = str => {
  if (!str) return '';
  return str.toString().toLowerCase();
};
export const getKeyFromObject = (obj, keyFragment) => {
  // Convert the key fragment to lowercase for case-insensitive search
  const lowerCaseKeyFragment = keyFragment.toLowerCase();

  // Iterate over the keys of the object
  for (let key in obj) {
    if (key.toLowerCase().includes(lowerCaseKeyFragment)) {
      const value = obj[key];
      // Check if the value is an array, and return the first item if it is
      if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null;
      }
      // Return the value if it is not an array
      return value;
    }
  }

  // Return null if no key is found
  return '';
};

/**
 * Formats a number (string with commas or numeric) into a compact, human-readable string
 * with metric suffixes
 * @param {number|string|null|undefined} value - The number to format (e.g., 200000 or "200,000")
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimals=1] - Number of decimal places
 * @returns {string|null} Formatted number string or null if invalid
 */
export const formatNumberCompact = (value, { decimals = 0 } = {}) => {
  // Handle invalid inputs
  if (value == null) return null;

  // Convert string with commas to a number by removing non-numeric characters (except minus and decimal)
  const cleanValue =
    typeof value === 'string'
      ? value.replace(/[^0-9.-]/g, '') // Remove commas and other invalid characters
      : value;

  const num = Number(cleanValue);

  // Check if conversion resulted in a valid number
  if (isNaN(num)) {
    console.warn(`Invalid number format: ${value}`); // Debug invalid inputs
    return null;
  }

  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Format based on magnitude
  if (absNum >= 1000000) {
    return `${isNegative ? '-' : ''}${(absNum / 1000000).toFixed(decimals)}M`;
  } else if (absNum >= 1000) {
    return `${isNegative ? '-' : ''}${(absNum / 1000).toFixed(decimals)}k`;
  }

  // Return as string for small numbers
  return num.toString();
};
const generateRandomString = length => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const generateUniqueDisplayName = listingTitle => {
  const randomString = generateRandomString(5); // Adjust the length as needed
  return `${listingTitle}${randomString}`;
};
export const normalizedStatus = status => status?.trim().toLowerCase();

// Helper function to sort options alphabetically using Intl.Collator and a specified key
export const sortOptionsByKeyIntl = (options, key, name) => {
  const collator = new Intl.Collator('en', { sensitivity: 'base' });

  // Filter out options with an empty key (after trimming)
  const validOptions = options.filter(option => option[key] && option[key].trim());

  // Remove duplicates while preserving order using trimmed key values
  const uniqueOptions = Array.from(
    new Set(validOptions.map(option => option[key].trim()))
  ).map(value => validOptions.find(option => option[key].trim() === value));

  // Sort the options alphabetically by the key
  uniqueOptions.sort((a, b) => collator.compare(a[key].trim(), b[key].trim()));

  // If name is "pub_talent_type", move "Athlete" to the top
  if (name === 'pub_talent_type') {
    const athleteIndex = uniqueOptions.findIndex(option => option.value === 'athlete');
    if (athleteIndex > -1) {
      const [athlete] = uniqueOptions.splice(athleteIndex, 1);
      uniqueOptions.unshift(athlete);
    }
  }

  return uniqueOptions;
};

export const extractStripePaymentMethodId = currentUser => {
  if (!currentUser || !currentUser.attributes || !currentUser.attributes.defaultPaymentMethod) {
    return null;
  }

  const { defaultPaymentMethod } = currentUser.attributes;

  if (defaultPaymentMethod.attributes && defaultPaymentMethod.attributes.stripePaymentMethodId) {
    return defaultPaymentMethod.attributes.stripePaymentMethodId;
  }

  return null;
};

/**
 * Filters an array of objects to exclude those with a `userType` in the `metadata`.
 * @param {Array} dataArray - The array of objects to filter.
 * @returns {Array} - Updated array with objects that don't have a `userType` in `metadata`.
 */
export function filterWithoutUserType(dataArray, currentUserId) {
  return dataArray.filter(item => item?.metadata?.authorId === currentUserId);
}

export function filterSubscriptionsByAuthor(dataArray, currentUserId) {
  if (!Array.isArray(dataArray) || !currentUserId) return [];

  return dataArray.filter(item => item?.metadata?.authorId === currentUserId);
}

export const cardRenderSizes = isMapVariant => {
  const panelMediumWidth = 50;
  const panelLargeWidth = 62.5;

  return isMapVariant
    ? [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ')
    : [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
};

export const formatStringtoCamelCase = str => {
  return str
    .split(' ') // Split the string into words
    .map(
      (word, index) =>
        index === 0
          ? word.toLowerCase() // Lowercase the first word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize the first letter of subsequent words
    )
    .join(''); // Join them back together
};

// Helper function to calculate age
export const calculateAge = dateOfBirth => {
  const dob = moment(dateOfBirth, 'MM/DD/YYYY'); // Adjust format if needed
  const currentDate = moment();
  return currentDate.diff(dob, 'years'); // Calculate difference in years
};

export const filterSocialByHandleKey = data => {
  return data.filter(item => item.hasOwnProperty('handle'));
};

export const filterListingsByBrandPremier = listings => {
  return listings.filter(
    listing =>
      listing?.author?.attributes?.profile?.publicData?.subscriptionType ===
      SUBSCRIPTION_TYPES.BRAND_PREMIER
  );
};

export const getDefaultAvatarAsFile = async defaultFanAvatar => {
  // Fetch the default image from your assets (or remote URL)
  const response = await fetch(defaultFanAvatar);
  const blob = await response.blob();

  // Create a File object from the blob
  const file = new File([blob], 'defaultFanAvatar.png', { type: blob.type });

  // Add a custom "path" property to the File object
  file.path = URL.createObjectURL(file);

  return file;
};

export const trimTextField = value => value?.trim() || '';

export const createMultiFilterOptions = options =>
  options.map(o => ({
    value: o.option ? o.option : o.key,
    label: o.label,
    ...(o.id && { id: o.id }),
  }));

export const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

/**
 * Converts a date into a human-readable "time ago" string.
 * @param {Date|string} createdAt - The date to compare against the current time.
 * @returns {string} - A string like "Posted 7 minutes ago", "Posted 1 day ago", etc.
 */
export const getTimeAgo = createdAt => {
  // Ensure createdAt is a Date object
  const createdDate = new Date(createdAt);
  const now = new Date();

  // Validate the date
  if (isNaN(createdDate.getTime())) {
    return 'Posted just now'; // Fallback for invalid dates
  }

  // Calculate the difference in milliseconds
  const diffInMs = now - createdDate;
  const diffInSeconds = Math.floor(diffInMs / 1000);

  // Define time intervals in seconds
  const intervals = {
    year: 31536000, // 365 * 24 * 60 * 60
    month: 2592000, // 30 * 24 * 60 * 60
    day: 86400, // 24 * 60 * 60
    hour: 3600, // 60 * 60
    minute: 60,
    second: 1,
  };

  // Handle "just now" for very recent posts
  if (diffInSeconds < 30) {
    return 'Posted just now';
  }

  // Calculate the appropriate time unit
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const value = Math.floor(diffInSeconds / secondsInUnit);
    if (value >= 1) {
      const unitLabel = value === 1 ? unit : `${unit}s`; // Pluralize if needed
      return `Posted ${value} ${unitLabel} ago`;
    }
  }

  // Fallback for edge cases
  return 'Posted just now';
};
export const createFilterOptions = options =>
  options.map(o => ({
    value: o.option ? o.option : o.key,
    label: o.label,
  }));

// Helper: get array of selected values from URL for a given param
export const getSelectedValues = (paramKey, location) => {
  const searchParams = new URLSearchParams(location.search);
  const existing = searchParams.get(paramKey);
  return existing ? existing.split(',') : [];
};

export const capitalizeFirstSpacer = str => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getSubmitButtonType = (userType, currentStep, TALENT, INITIAL) => {
  if (userType === TALENT && currentStep === INITIAL) {
    return 'button';
  } else if (userType === TALENT && currentStep !== INITIAL) {
    return 'submit';
  } else if (userType !== TALENT && currentStep === INITIAL) {
    return 'submit';
  } else {
    return 'submit';
  }
};

export const sortServicesTypesAlphabetically = arr => [...arr].sort((a, b) => a.localeCompare(b));

export const bytesToMB = bytes => {
  if (!bytes || isNaN(bytes)) {
    return 'N/A'; // Return an empty string or a placeholder if bytes is invalid
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const getAcceptedTypes = activeContentType => {
  switch (activeContentType) {
    case 'photos':
      return { 'image/*': [] };
    case 'videos':
      return { 'video/*': [] };
    case 'posts':
      return { 'image/*': [], 'video/*': [] };
    default:
      return { 'image/*': [], 'video/*': [] };
  }
};
// Helper to strip the extension from a file name
export const getBaseName = (fullName = '') => {
  // If there's no dot, return the full name. Otherwise, remove everything from the last dot onward.
  return fullName.replace(/\.[^/.]+$/, '');
};

export const getFirstListingImageUrl = (listing, variant = 'square-small2x') => {
  if (
    listing &&
    Array.isArray(listing.images) &&
    listing.images.length > 0 &&
    listing.images[0]?.attributes?.variants?.[variant]?.url
  ) {
    return listing.images[0].attributes.variants[variant].url;
  }
  return null;
};

export const getLocationSectionVisibility = ({ userType, talentLevel, talentType }) => {
  const isEligibleForSchoolAndConference =
    talentLevel === TalentLevel.COLLEGIATE || talentLevel === TalentLevel.HIGH_SCHOOL;

  const isCollegiateAthlete =
    talentLevel === TalentLevel.COLLEGIATE && talentType === TalentType.ATHLETE;
  const isNotHighSchool = talentLevel !== TalentLevel.HIGH_SCHOOL;

  const showSchoolFields = isNotHighSchool || isCollegiateAthlete;

  const showFormBlock = userType === UserType.TALENT && isEligibleForSchoolAndConference;

  const showHighSchoolNameField = talentLevel === TalentLevel.HIGH_SCHOOL;
  const showCityStateFields = !isEligibleForSchoolAndConference || showFormBlock;
  const showReadOnlySchoolLocation = talentLevel === TalentLevel.COLLEGIATE;

  return {
    isEligibleForSchoolAndConference,
    showSchoolFields,
    showFormBlock,
    showHighSchoolNameField,
    showCityStateFields,
    showReadOnlySchoolLocation,
  };
};

export const sanitizeUpdateValues = updateValues => {
  const cleanedPublicData = Object.entries(updateValues.publicData || {}).reduce(
    (acc, [key, value]) => {
      // Remove the key if value is an empty array
      if (Array.isArray(value) && value.length === 0) {
        return acc;
      }

      // Remove the key if it's an object but empty (e.g., {}) or null
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0
      ) {
        return acc;
      }

      // Special case: remove 'autographs' if it doesn't have 'typeOfItems'
      if (
        key === 'autographs' &&
        (!value ||
          typeof value !== 'object' ||
          !value.typeOfItems ||
          typeof value.typeOfItems !== 'object' ||
          Object.keys(value.typeOfItems).length === 0)
      ) {
        return acc;
      }

      acc[key] = value;
      return acc;
    },
    {}
  );

  return {
    ...updateValues,
    publicData: cleanedPublicData,
  };
};
export const getImageVariantConfigs = (variantPrefix, aspectRatio) => ({
  ...createImageVariantConfig(variantPrefix, IMAGE_VARIANTS.SIZES.BASE, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-2x`, IMAGE_VARIANTS.SIZES.DOUBLE, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-4x`, IMAGE_VARIANTS.SIZES.QUAD, aspectRatio),
  ...createImageVariantConfig(`${variantPrefix}-6x`, IMAGE_VARIANTS.SIZES.SEXTUPLE, aspectRatio),
});

export const isVideoFile = fileName => /\.(mp4|mov|webm)$/i.test(fileName);

export const getCombinedListingCategory = (pub_listingCategory, jobCategoryFilter) => {
  if (!pub_listingCategory) return null;

  // Capitalize first letter of jobCategoryFilter if present
  const suffix = jobCategoryFilter
    ? jobCategoryFilter.charAt(0).toUpperCase() + jobCategoryFilter.slice(1)
    : '';

  return `${pub_listingCategory}${suffix}`;
};

export const updateUnlockablePurchases = (existingPurchases, params, paymentIntentId) => {
  const { itemToBuy, listingId, activeTab } = params;

  const existingPurchaseIndex = existingPurchases.findIndex(
    purchase => purchase.listingId === listingId
  );

  if (existingPurchaseIndex !== -1) {
    const updatedPurchases = [...existingPurchases];
    const existingMedia = updatedPurchases[existingPurchaseIndex].media[activeTab] || [];

    const existingMediaIndex = existingMedia.findIndex(media => media.mediaKey === itemToBuy?.key);

    if (existingMediaIndex !== -1) {
      updatedPurchases[existingPurchaseIndex].media[activeTab][existingMediaIndex] = {
        mediaKey: itemToBuy?.key,
        paymentIntentId,
      };
    } else {
      updatedPurchases[existingPurchaseIndex].media[activeTab] = [
        ...existingMedia,
        {
          mediaKey: itemToBuy?.key,
          paymentIntentId,
        },
      ];
    }

    return updatedPurchases;
  } else {
    return [
      ...existingPurchases,
      {
        listingId,
        media: {
          [activeTab]: [
            {
              mediaKey: itemToBuy?.key,
              paymentIntentId,
            },
          ],
        },
      },
    ];
  }
};

export function convertToCents(amount) {
  if (amount == null || isNaN(amount)) {
    return 0;
  }

  const parsedAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return 0;
  }

  return Math.round(parsedAmount * 100);
}

// Helper function to determine label ID
export const getTalentLabelId = userType => {
  const labelMap = {
    [UserType.SCHOOL]: 'PerksPostForm.allGroupTalent',
    [UserType.AGENCY]: 'PerksPostForm.allAgencyTalent',
    [UserType.BRAND]: 'PerksPostForm.allTalent',
  };
  return labelMap[userType] || 'PerksPostForm.allTalent'; // Fallback to default
};

// Helper function to determine service level configuration
export const getServiceLevelConfig = userType => {
  const baseConfig = [
    {
      id: 'diy',
      value: 'diy',
      baseLabelId: 'CampaignPostingForm.serviceLevel.diy',
      infoId: 'CampaignPostingForm.serviceLevel.diyInfo',
    },
    {
      id: 'assist',
      value: 'assist',
      baseLabelId: 'CampaignPostingForm.serviceLevel.assist',
      infoId: 'CampaignPostingForm.serviceLevel.assistInfo',
    },
    {
      id: 'complete',
      value: 'complete',
      baseLabelId: 'CampaignPostingForm.serviceLevel.complete',
      infoId: 'CampaignPostingForm.serviceLevel.completeInfo',
    },
  ];

  if (userType === UserType.AGENCY) {
    return baseConfig.filter(item => item.id !== 'complete');
  }
  return baseConfig; // Default includes all (e.g., for BRAND)
};

// Helper function to determine dynamic label ID
export const getServiceLevelLabelId = (userType, baseLabelId) => {
  const labelOverrides = {
    [UserType.AGENCY]: {
      'CampaignPostingForm.serviceLevel.diy': 'CampaignPostingForm.serviceLevel.agencyTalent',
      'CampaignPostingForm.serviceLevel.assist':
        'CampaignPostingForm.serviceLevel.assistMarketplace',
    },
  };
  return labelOverrides[userType]?.[baseLabelId] || baseLabelId;
};

// Helper function to dynamically format primary industry
export const formatPrimaryIndustry = industry => {
  if (!industry) return '';

  // Split by hyphen, capitalize each word, and join with spaces
  return industry
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
export const toKebabCase = str => {
  return str
    .trim() // Remove leading/trailing spaces
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace one or more spaces with a hyphen
    .replace(/[^a-z0-9-]/g, ''); // Remove non-alphanumeric characters except hyphens
};

export const getSubTypes = (items, key) =>
  isArrayLength(items)
    ? [...new Set(items.filter(item => item.selected === true && item[key]).map(item => item[key]))]
    : [];

// Helper fn
export const getCategoryFromLocation = (location, categories) => {
  if (!location || !location.search) return null;

  // Use URLSearchParams to parse query
  const params = new URLSearchParams(location.search);
  const categoryParam = params.get('pub_listingCategory');

  if (!categoryParam) return null;

  // Normalize to lowercase to avoid case mismatch
  const normalized = categoryParam.toLowerCase();

  // Find the matching category from the array
  const matched = categories.find(c => c.key.toLowerCase() === normalized);

  return matched || null;
};

export const getStateFromHistory = () => {
  if (typeof window !== 'undefined' && !window.history) return null;
  return window.history.state; // Access the latest state pushed via history
};

// Helper function to capitalize words (handles hyphen-separated strings)
export const capitalizeHyphenated = str => {
  if (!str) return '';
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to safely parse JSON
export const safeJsonParse = (jsonString, defaultValue = null) => {
  if (!jsonString || typeof jsonString !== 'string') {
    return defaultValue;
  }
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return defaultValue;
  }
};

// ============================================
// Helper function to format Unix timestamp
// ============================================
export const formatUnixDate = unixTimestamp => {
  if (!unixTimestamp) return null;
  try {
    return new Date(unixTimestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

export const extractFileExtension = file => {
  // Check if file is valid and has a 'name' property
  if (!file || !file.name) {
    return null; // Return null if file or file.name is not defined
  }

  // Extract and return the file extension
  return file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
};

export const copyToClipboard = async text => {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus({
      preventScroll: true,
    });
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  } catch (e) {
    console.error(`Copying failed with error: ${e}`);
    throw e;
  }
};

export const getStateName = stateCode => {
  if (!stateCode) return stateCode; // handle null/undefined/empty

  const trimmed = String(stateCode)
    .trim()
    .toUpperCase();
  const state = US_STATES.find(s => s.code === trimmed);

  return state ? state.name : stateCode; // fallback to original if not found
};

export const parseMessageContent = rawText => {
  const parsedSystemMessage = parseChatSystemMessage(rawText);
  if (parsedSystemMessage.isSystem) {
    return {
      isSystem: true,
      messageText: null,
      attachments: parsedSystemMessage.attachments || [],
      parsedMessage: parsedSystemMessage,
    };
  } else {
    try {
      const parsedJson = JSON.parse(rawText);
      return {
        isSystem: false,
        messageText: parsedJson.messageText || null,
        attachments: parsedJson.attachments || [],
      };
    } catch (e) {
      return {
        isSystem: false,
        messageText: rawText,
        attachments: [],
      };
    }
  }
};
